import React, { useState, useEffect, useRef } from "react";
import TravellerVerificationForm from "../TravellerVerificationForm";
import { useDispatch, useSelector } from 'react-redux';
import { verifyDocument, getDocument } from '../../store/Verify/VerifyAction';
import { verifyAction } from '../../store/Verify/VerifyReducer';
import { visaApplied } from "../../store/VisaApplied/VisaAppliedAction";
import { visaAppliedAction } from "../../store/VisaApplied/VisaAppliedReducer";
import Lottie from 'lottie-react';
import passportVerify from '../../animation/passportVerify.json';
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import Loading from "../../components/Loading";
import heic2any from "heic2any";

const DocumentUpload = ({
  numOfTravellers,
  visaDetail,
  currentTraveller,
  sessionId,
  departureDate,
  currency,
  minSelectableDate
}) => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const passportFrontInputRef = useRef(null);
  const passportBackInputRef = useRef(null);
  const usVisaInputRef = useRef(null);

  const [photo, setPhoto] = useState({});
  const [passportFront, setPassportFront] = useState({});
  const [passportBack, setPassportBack] = useState({});
  const [usVisa, setUsVisa] = useState("");
  const [changeFormData, setChangeFormData] = useState(true);
  const [changeDocument, setChangeDocument] = useState(false);
  const [passportVerified, setPassportVerified] = useState(false);
  const [passportVerifiedError, setPassportVerifiedError] = useState('');
  const [photoVerified, setPhotoVerified] = useState(false);
  const [photoVerifiedError, setPhotoVerifiedError] = useState('');
  const [usVisaVerified, setUsVisaVerified] = useState(false);
  const [usVisaVerifiedError, setUsVisaVerifiedError] = useState('');
  const [extractData, setExtractData] = useState({});
  const dispatch = useDispatch();

  const { verify, verifySuccess, verifyLoading, verifyError, documentLoading, document, documentSuccess } = useSelector((state) => state.verify);
  const { visaAppliedLoading, visaAppliedError, visaAppliedSuccess, visaAppliedDetail } = useSelector((state) => state.visaApplied);

  useEffect(() => {
    if (verifySuccess) {
      let { photo, passport, usVisa } = verify;
      let validationFailed = false;
      if (photo) {
        if (photo?.verified === "success") {
          setPhotoVerified(true);
          setPhotoVerifiedError('');
        } else {
          setPhotoVerified(false);
          setPhotoVerifiedError('Photo is not Valid');
          validationFailed = true;
        }
      }

      if (passport) {
        if (passport?.verified === "success") {
          setPassportVerified(passport?.verified === "success");
          setExtractData(passport?.details);
          setPassportVerifiedError('');
        } else {
          setPassportVerified(false);
          setPassportVerifiedError('Passport is not Valid');
          validationFailed = true;
        }
      }
      if (usVisa) {
        if (usVisa?.verified === "success") {
          setUsVisaVerified(true);
          setUsVisaVerifiedError('');
        } else {
          setUsVisaVerified(false);
          setUsVisaVerifiedError('US Visa is not Valid');
          validationFailed = true;
        }
      }
      if (validationFailed) {
        toast.error("Hmm... your documents didn’t pass. Try uploading a different one!", {
          position: "top-right",
          bodyStyle: { fontSize: "16px" },
        });
      } else {
        toast.success("All set! Your documents look good. You’re good to go!", {
          position: "top-right",
          bodyStyle: { fontSize: "16px" },
        });
      }
      dispatch(verifyAction.clearVerifySuccess());
    }
    if (verifyError) {
      toast.error("Uh-oh! Something went wrong during verification. Try verification again.", {
        position: "top-right",
        bodyStyle: { fontSize: "16px" },
      });
      dispatch(verifyAction.clearError());
    }
  }, [verify, verifySuccess, dispatch, currentTraveller, verifyError]);

  useEffect(() => {
    if (documentSuccess) {
      const { photo, passport, usVisa } = document;
      if (photo) {
        setPhoto(photo);
        if (photo?.verified === "success") {
          setPhotoVerified(true);
          setPhotoVerifiedError('');
        } else {
          setPhotoVerified(false);
          setPhotoVerifiedError('Photo is not Valid');
        }
      }
      if (passport) {
        setPassportFront(passport);
        setPassportBack(passport);
        if (passport?.verified === "success") {
          setPassportVerified(true);
          setExtractData(passport?.details);
          setPassportVerifiedError('');
        } else {
          setPassportVerified(false);
          setPassportVerifiedError('Passport is not Valid');
        }
      }
      if (usVisa) {
        setUsVisa(usVisa);
        if (usVisa?.verified === "success") {
          setUsVisa(usVisa);
          setUsVisaVerified(true);
          setUsVisaVerifiedError('');
        } else {
          setUsVisaVerified(false);
          setUsVisaVerifiedError('US Visa is not Valid');
        }
      }
      dispatch(verifyAction.clearGetDocumentSuccess());
      dispatch(verifyAction.clearGetDocument());
    }
  }, [document, documentSuccess, dispatch]);


  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getDocument({ sessionId, currentTraveller }));
  }, [currentTraveller, dispatch, sessionId]);

  useEffect(() => {
    if (visaAppliedSuccess) {
      navigate(
        `/payment/${visaAppliedDetail._id}`
      );
      dispatch(visaAppliedAction.clearSuccess());
    }

    if (visaAppliedError) {
      toast.error('Failed to fetch payment page. Try Again....')
      dispatch(visaAppliedAction.clearError());
    }
  }, [visaAppliedSuccess, visaAppliedError, dispatch, navigate, visaAppliedDetail])

  const handleNext = () => {
    if (changeFormData) {
      alert("You have unsaved data. Please save before proceeding.");
    } else if (currentTraveller < numOfTravellers) {
      navigate(
        `/applyVisa/${visaDetail?._id}?sessionId=${sessionId}&departureDate=${departureDate}&numTravellers=${numOfTravellers}&currentTraveller=${currentTraveller + 1}`
      );
    }
  };

  const handlePrev = () => {
    if (changeFormData) {
      alert("You have unsaved data. Please save before proceeding.");
    } else if (currentTraveller > 1) {
      navigate(
        `/applyVisa/${visaDetail?._id}?sessionId=${sessionId}&departureDate=${departureDate}&numTravellers=${numOfTravellers}&currentTraveller=${currentTraveller - 1}&page=document`
      );
    }
  };

  const handleContinue = () => {
    if (changeFormData) {
      alert("You have unsaved data. Please save before proceeding.");
    } else {
      const confirmProceed = window.confirm(
        "Before moving forward, please double-check the traveler's details. Are you sure everything is accurate and ready to proceed?"
      );
      if (confirmProceed) {
        dispatch(visaApplied({
          visaId: visaDetail._id,
          departureDate,
          numTravellers: numOfTravellers,
          deliveryDate: minSelectableDate,
          sessionId,
          currency,
        }));
      }
    }
  };



  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 2MB
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/heic"];

  const handleFileChange = async (e, setFile, fileName) => {
    let file = e.target.files[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, PNG, WebP or HEIC images are allowed.");
      return;
    }

    // Convert HEIC to JPEG
    if (file.type === "image/heic") {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        file = new File([convertedBlob], file.name.replace(/\.heic$/, ".jpg"), {
          type: "image/jpeg",
        });
      } catch (err) {
        console.error("HEIC conversion failed", err);
        alert("Couldn't convert HEIC image. Try a different image.");
        return;
      }
    }

    // Check size after conversion
    if (file.size > MAX_IMAGE_SIZE) {
      alert(`This image is too large. Max size is 10MB.`);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const base64String = e.target.result.split(",")[1];
      const fileData = {
        name: file.name,
        mimeType: file.type,
        data: base64String,
      };
      setFile(fileData);
      setChangeDocument(true);

      if (fileName === "photo") {
        setPhotoVerified(false);
        setPhotoVerifiedError('');
      }

      if (fileName === "passportFront" || fileName === "passportBack") {
        setPassportVerified(false);
        setPassportVerifiedError('');
      }

      if (fileName === "usVisa") {
        setUsVisaVerified(false);
        setUsVisaVerifiedError('');
      }
    };

    reader.readAsDataURL(file);
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    const travellerData = {
      photo,
      passport: {
        passportFrontImage: passportFront,
        passportBackImage: passportBack
      },
      usVisa
    };
    setChangeDocument(false);
    dispatch(verifyDocument({ document: travellerData, currentTraveller, sessionId }));
  };

  // Check if all required files are uploaded
  const areAllFilesUploaded = () => {
    return (
      (!visaDetail?.documentRequirement?.photo || (photo && Object.keys(photo).length > 0)) &&
      (!visaDetail?.documentRequirement?.passport ||
        (passportFront && passportBack && Object.keys(passportFront).length > 0 && Object.keys(passportBack).length > 0)) &&
      (!visaDetail?.documentRequirement?.usVisa || (usVisa && Object.keys(usVisa).length > 0)) && !allRequiredVerified()
      && passportVerifiedError.length === 0
      && photoVerifiedError.length === 0
      && usVisaVerifiedError.length === 0
    );
  };

  const allRequiredVerified = () => {
    return (
      (!visaDetail?.documentRequirement?.photo || (photo && photoVerified)) &&
      (!visaDetail?.documentRequirement?.passport ||
        (passportBack && passportFront && passportVerified)) &&
      (!visaDetail?.documentRequirement?.usVisa || (usVisa && usVisaVerified))
      && !changeDocument
    );
  };

  return (<>
    {documentLoading || visaAppliedLoading ? (<Loading></Loading>) : (<div className="documentUpload">
      {verifyLoading && <div className='documentUpload__passportVerify'>
        <Lottie
          animationData={passportVerify}
          loop={true}
          style={{ height: 400, width: '70%' }}
        />
      </div>}
      <form className="documentUpload__form">
        <div className="documentUpload__heading">
          Traveller {currentTraveller}
        </div>
        <div className="documentUpload__form-box">
          {visaDetail?.documentRequirement?.photo && (
            <div className="documentUpload__input-wrapper">
              <div className="documentUpload__upload-section">
                <div className="documentUpload__upload-section--text">
                  <svg
                    className="documentUpload__upload-icon1"
                    aria-hidden="true"
                  >
                    <use href="\sprite.svg#icon-tick"></use>
                  </svg>
                  <span className="documentUpload__upload-text">Photo</span>
                </div>

                <input
                  type="file"
                  accept="image/jpeg, image/png, image/webp, .heic"
                  onChange={(e) => handleFileChange(e, setPhoto, "photo")}
                  style={{ display: "none" }}
                  ref={photoInputRef}
                  required
                />
                {(photo && Object.keys(photo).length > 0) && <svg
                  className="documentUpload__upload-icon2"
                  aria-hidden="true"
                  onClick={() => photoInputRef.current.click()}
                >
                  <use href="\sprite.svg#icon-edit"></use>
                </svg>}
              </div>
              {(!photo || Object.keys(photo).length === 0) && (
                <img
                  src="/images/upload.png"
                  alt="Uploaded"
                  className="documentUpload__upload-preview"
                  onClick={() => photoInputRef.current.click()}
                />
              )}
              {(photo && Object.keys(photo).length > 0) && (
                <img
                  src={photo.image
                    ? photo.image
                    : `data:${photo.mimeType};base64,${photo.data}`}
                  alt="Uploaded document"
                  className="documentUpload__preview"
                />
              )}
              {(!photoVerified && photoVerifiedError.length > 0) && <div className='documentUpload__error'>{photoVerifiedError}</div>}
            </div>
          )}

          {visaDetail?.documentRequirement?.passport && (
            <div className="documentUpload__input-wrapper">
              <div className="documentUpload__upload-section">
                <div className="documentUpload__upload-section--text">
                  <svg
                    className="documentUpload__upload-icon1"
                    aria-hidden="true"
                  >
                    <use href="\sprite.svg#icon-tick"></use>
                  </svg>
                  <span className="documentUpload__upload-text">
                    Passport Front
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/webp, .heic"
                  onChange={(e) =>
                    handleFileChange(e, setPassportFront, "passportFront")
                  }
                  style={{ display: "none" }}
                  ref={passportFrontInputRef}
                  required
                />
                {(passportFront && Object.keys(passportFront).length > 0) && <svg
                  className="documentUpload__upload-icon2"
                  aria-hidden="true"
                  onClick={() => passportFrontInputRef.current.click()}
                >
                  <use href="\sprite.svg#icon-edit"></use>
                </svg>}
              </div>
              {(!passportFront || Object.keys(passportFront).length === 0) && (
                <img
                  src="/images/upload.png"
                  alt="Uploaded Passport Front"
                  className="documentUpload__upload-preview"
                  onClick={() => passportFrontInputRef.current.click()}
                />
              )}
              {(passportFront && Object.keys(passportFront).length > 0) && (
                <img
                  src={passportFront?.frontImage ? passportFront.frontImage : `data:${passportFront.mimeType};base64,${passportFront.data}`}
                  alt="Uploaded Passport Front"
                  className="documentUpload__preview"
                />
              )}
              {(!passportVerified && passportVerifiedError.length > 0) && <div className='documentUpload__error'>{passportVerifiedError}</div>}
            </div>
          )}

          {visaDetail?.documentRequirement?.passport && (
            <div className="documentUpload__input-wrapper">
              <div className="documentUpload__upload-section">
                <div className="documentUpload__upload-section--text">
                  <svg
                    className="documentUpload__upload-icon1"
                    aria-hidden="true"
                  >
                    <use href="\sprite.svg#icon-tick"></use>
                  </svg>
                  <span className="documentUpload__upload-text">
                    Passport Back
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/webp, .heic"
                  onChange={(e) =>
                    handleFileChange(e, setPassportBack, "passportBack")
                  }
                  style={{ display: "none" }}
                  ref={passportBackInputRef}
                  required
                />
                {(passportBack && Object.keys(passportBack).length > 0) && <svg
                  className="documentUpload__upload-icon2"
                  aria-hidden="true"
                  onClick={() => passportBackInputRef.current.click()}
                >
                  <use href="\sprite.svg#icon-edit"></use>
                </svg>}
              </div>
              {(!passportBack || Object.keys(passportBack).length === 0) && (
                <img
                  src="/images/upload.png"
                  alt="Uploaded Passport Back"
                  className="documentUpload__preview"
                  onClick={() => passportBackInputRef.current.click()}
                />
              )}
              {(passportBack && Object.keys(passportBack).length > 0) && (
                <img
                  src={passportBack?.backImage ? passportBack?.backImage : `data:${passportBack.mimeType};base64,${passportBack.data}`}
                  alt="Uploaded Passport Back"
                  className="documentUpload__preview"
                />
              )}
            </div>
          )}

          {visaDetail?.documentRequirement?.usVisa && (
            <div className="documentUpload__input-wrapper">
              <div className="documentUpload__upload-section">
                <div className="documentUpload__upload-section--text">
                  <svg
                    className="documentUpload__upload-icon1"
                    aria-hidden="true"
                  >
                    <use href="\sprite.svg#icon-tick"></use>
                  </svg>
                  <span className="documentUpload__upload-text">
                    US/Schengen Visa
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/webp, .heic"
                  onChange={(e) =>
                    handleFileChange(e, setUsVisa, "usVisa")
                  }
                  style={{ display: "none" }}
                  ref={usVisaInputRef}
                  required
                />
                {(usVisa && Object.keys(usVisa).length > 0) && <svg
                  className="documentUpload__upload-icon2"
                  aria-hidden="true"
                  onClick={() => usVisaInputRef.current.click()}
                >
                  <use href="\sprite.svg#icon-edit"></use>
                </svg>}
              </div>
              {(!usVisa || Object.keys(usVisa).length === 0) && (
                <img
                  src="/images/upload.png"
                  alt="Uploaded US Visa"
                  className="documentUpload__preview"
                  onClick={() => usVisaInputRef.current.click()}
                />
              )}
              {(usVisa && Object.keys(usVisa).length > 0) && (
                <img
                  src={usVisa?.image ? usVisa.image : `data:${usVisa.mimeType};base64,${usVisa.data}`}
                  alt="Uploaded US Visa"
                  className="documentUpload__preview"
                />
              )}
              {(!usVisaVerified && usVisaVerifiedError.length > 0) && <div className='documentUpload__error'>{usVisaVerifiedError}</div>}
            </div>
          )}
        </div>
        <div className="documentUpload__submit">
          <button
            type="submit"
            className="documentUpload__submit-button"
            disabled={!areAllFilesUploaded()} // Disable button if not all files are uploaded
            onClick={handleSubmit}
          >
            Verify
          </button>
        </div>
      </form>
      {allRequiredVerified() && (
        <TravellerVerificationForm
          travellerDetail={extractData}
          setChangeFormData={setChangeFormData}
          sessionId={sessionId}
          currentTraveller={currentTraveller}
          changeFormData={changeFormData}
        />
      )}
      <div className="documentUpload__button">
        <button
          onClick={handlePrev}
          disabled={currentTraveller === 1}
          className="documentUpload__button-action"
        >
          Back
        </button>
        {currentTraveller !== numOfTravellers && (
          <button
            onClick={handleNext}
            disabled={!allRequiredVerified()}
            className="documentUpload__button-action"
          >
            Next
          </button>
        )}
        {currentTraveller === numOfTravellers && (
          <button
            disabled={!allRequiredVerified()}
            onClick={handleContinue}
            className="documentUpload__button-action"
          >
            Continue
          </button>
        )}
      </div>
    </div>)}
  </>
  );
};

export default DocumentUpload;
